/* Recheck configured flavor availability and dates before meal-box checkout.
   Keep hidden component cleanup for older carts. Do not enable component sales. */
(function () {
  'use strict';
  if (window.__babCartSync) return;
  window.__babCartSync = true;
  var policy = window.AlphaMealFlavors;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var inFlight = null;
  var timer;
  var checkoutBusy = false;
  var messages = {};

  async function cartRequest(path, body) {
    var response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(15000),
    });
    var result = await response.json();
    if (!response.ok) throw new Error(result.description || 'We could not check your meal boxes. Please try again.');
    return result;
  }

  function isBox(item) {
    return item.properties && item.properties._bundle_id && !item.properties._bundle_component;
  }

  function paint() {
    document.querySelectorAll('[data-bab-box-line]').forEach(function (el) {
      var message = messages[el.dataset.bundleId];
      if (message === undefined) return;
      el.textContent = message;
      el.hidden = !message;
    });
  }

  function showCheckoutError(text) {
    document.querySelectorAll('[data-cart-checkout-buttons]').forEach(function (buttons) {
      var el = buttons.querySelector('[data-bab-cart-error]');
      if (!el) {
        el = document.createElement('p');
        el.dataset.babCartError = '';
        el.setAttribute('role', 'alert');
        buttons.prepend(el);
      }
      el.textContent = text;
      el.hidden = !text;
    });
  }

  function boxSnapshot(items) {
    return JSON.stringify(items.filter(isBox).map(function (item) {
      return [item.properties._bundle_id, item.variant_id, item.quantity,
        item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id,
        Object.keys(item.properties).sort().map(function (key) { return [key, item.properties[key]]; })];
    }).sort(function (a, b) { return String(a[0]).localeCompare(String(b[0])); }));
  }

  async function check(attempt) {
    var cart = await cartRequest('cart.js');
    var items = cart.items || [];
    var parents = new Set(items.filter(isBox).map(function (item) { return item.properties._bundle_id; }));
    var removals = {};
    items.forEach(function (item) {
      var p = item.properties || {};
      if (p._bundle_component && p._bundle_id && !parents.has(p._bundle_id)) removals[item.key] = 0;
    });
    var changed = false;
    if (Object.keys(removals).length) {
      cart = await cartRequest('cart/update.js', { updates: removals });
      items = cart.items;
      changed = true;
    }
    var boxes = items.filter(isBox);
    var configs = {};
    var errors = [];
    messages = {};
    for (var item of boxes) {
      var p = item.properties;
      try {
        if (!policy) throw new Error('Please refresh to check flavor availability.');
        var configKey = String(item.product_id);
        if (!configs[configKey]) configs[configKey] = await policy.load(item.url, p._bab_section_id);
        var config = configs[configKey];
        if (String(config.productId) !== String(item.product_id)) throw new Error('Please rebuild this meal box.');
        var selections = policy.cartSelections(item, config);
        var result = policy.box(config, selections, item.quantity, policy.purchaseContextForCart(item));
        if (result.errors.length) throw new Error(result.errors.join(' '));
        // Keep this slot for validation errors; the truck notice covers shipping.
        messages[p._bundle_id] = '';
        var next = policy.propertiesForShipping(p, result.shipping);
        next = policy.propertiesForCheckoutNotice(next, config.checkoutNotice);
        var managedKeys = ['_preorder_ship_date', '_preorder_ship_label', '_preorder_type', policy.checkoutNoticeKey];
        if (managedKeys.some(function (key) { return (next[key] || '') !== (p[key] || ''); })) {
          var body = { id: item.key, quantity: item.quantity, properties: next };
          if (item.selling_plan_allocation) body.selling_plan = item.selling_plan_allocation.selling_plan.id;
          // Do not overwrite a quantity/property edit made while settings loaded.
          var beforeChange = await cartRequest('cart.js');
          var current = beforeChange.items.find(function (line) { return line.key === item.key; });
          if (!current || boxSnapshot([current]) !== boxSnapshot([item])) {
            if (!attempt) return check(1);
            throw new Error('Your cart changed. Please check your boxes and try checkout again.');
          }
          // Sequential: changing properties changes the line key. Never mutate in parallel.
          var updated = await cartRequest('cart/change.js', body);
          var updatedItem = updated.items.find(function (line) { return isBox(line) && line.properties._bundle_id === p._bundle_id; });
          if (!updatedItem) throw new Error('Your cart changed. Please try again.');
          if (managedKeys.some(function (key) { return (updatedItem.properties[key] || '') !== (next[key] || ''); })) {
            throw new Error('We could not update your meal box shipping notice. Please refresh and try again.');
          }
          Object.assign(item, updatedItem);
          changed = true;
        }
      } catch (error) {
        messages[p._bundle_id] = error.message;
        errors.push(error.message);
      }
    }
    if (boxes.length) {
      var finalCart = await cartRequest('cart.js');
      if (boxSnapshot(items) !== boxSnapshot(finalCart.items)) {
        if (!attempt) return check(1);
        errors.push('Your cart changed. Please check your boxes and try checkout again.');
      }
    }
    paint();
    showCheckoutError(errors.length ? errors[0] : '');
    if (changed) document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    return { valid: !errors.length, hasBoxes: !!boxes.length };
  }

  function sync() {
    if (inFlight) return inFlight;
    inFlight = check().catch(function (error) {
      showCheckoutError(error.message);
      return { valid: false };
    }).finally(function () { inFlight = null; });
    return inFlight;
  }

  function schedule() {
    clearTimeout(timer);
    paint();
    timer = setTimeout(sync, 150);
  }

  // Checkout always gets a fresh check, including when the page was left open.
  // Keep the existing form submission so terms, discounts and cart notes survive.
  async function checkout(form, submitter, href) {
    if (checkoutBusy) return;
    checkoutBusy = true;
    clearTimeout(timer);
    try {
      if (inFlight) await inFlight;
      var result = await sync();
      if (!result.valid) return;
      if (form) {
        // A properties refresh can replace the original cart form/button.
        var current = form.isConnected ? form : document.querySelector('form[action="' + root + 'cart"]');
        if (!current) { window.location.assign(root + 'checkout'); return; }
        var button = submitter && submitter.isConnected ? submitter : current.querySelector('[name="checkout"]');
        current.dataset.babValidated = 'true';
        current.requestSubmit(button || undefined);
        delete current.dataset.babValidated;
      } else {
        window.location.assign(href);
      }
    } finally { checkoutBusy = false; }
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form.dataset.babValidated === 'true') return;
    if (!form.querySelector('[name="checkout"]')) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    if (!document.querySelector('[data-bab-box-line]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(form, event.submitter);
  }, true);

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href]');
    if (!link || !document.querySelector('[data-bab-box-line]')) return;
    var url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(null, null, url.href);
  }, true);

  document.addEventListener('theme:cart:change', schedule);
  document.addEventListener('theme:cart:update', schedule);
  document.addEventListener('theme:cart-drawer:open', schedule);
  window.addEventListener('pageshow', function (event) { if (event.persisted) schedule(); });
  if (document.readyState !== 'loading') schedule();
  else document.addEventListener('DOMContentLoaded', schedule);
})();
